/* Amounts arrive as integer strings in a token's base units, alongside
   `decimals` (how many the token has) and `significantDecimals` (how many to
   show). An 18-decimal value does not survive a float, so everything here
   stays in BigInt until the final string. */

export type TokenScale = { decimals: number; significantDecimals: number };

const groupThousands = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/* Truncating is right for a balance — it never claims more than is there. It is
   wrong for a *minimum*, where the truncated figure sits below the limit it
   states, so a player who types exactly what we printed is refused. Those call
   sites round up instead. */
const round = (
  value: bigint,
  decimals: number,
  significantDecimals: number,
  rounding: "down" | "up" = "down",
) => {
  const step = 10n ** BigInt(Math.max(0, decimals - significantDecimals));

  if (rounding === "down" || step === 1n) {
    return value;
  }

  return ((value + step - 1n) / step) * step;
};

export const formatAmount = (
  value: string,
  { decimals, significantDecimals }: TokenScale,
  options: { grouped?: boolean; rounding?: "down" | "up" } = {},
) => {
  const negative = value.startsWith("-");
  const raw = negative ? value.slice(1) : value;

  if (!/^\d+$/.test(raw)) {
    return "0";
  }

  const scaled = round(BigInt(raw), decimals, significantDecimals, options.rounding);
  const divisor = 10n ** BigInt(decimals);
  const whole = scaled / divisor;
  const remainder = scaled % divisor;

  const wholeText = options.grouped === false ? whole.toString() : groupThousands(whole.toString());
  const sign = negative && scaled !== 0n ? "-" : "";

  if (significantDecimals <= 0) {
    return `${sign}${wholeText}`;
  }

  const fraction = remainder.toString().padStart(decimals, "0").slice(0, significantDecimals);

  return `${sign}${wholeText}.${fraction}`;
};

/* An amount the API stated, as a number we can compare.

   Every base-unit field arrives as an integer string, and `BigInt()` throws on
   anything else — inside a render that is a blank modal rather than a wrong
   figure. Comparisons against a limit go through here so a surprise in the
   payload costs the limit, not the screen. */
export const toBaseUnits = (value: string | null | undefined) =>
  typeof value === "string" && /^-?\d+$/.test(value) ? BigInt(value) : 0n;

/* The inverse, for the one direction that goes the other way: a withdrawal
   amount typed by a player.

   `POST /withdraw/{routeId}` takes `amount` in the token's base units, the same
   integer string every balance arrives as — so posting the typed "10.5"
   verbatim is off by a factor of 10^decimals and the API rejects it. Commas
   are read as decimal separators, which is what a European keyboard produces;
   a grouped "1,000.5" therefore fails, which is the safe way to fail. */
export const parseAmount = (value: string, decimals: number): bigint | null => {
  const normalised = value.trim().replace(/,/g, ".");

  if (!/^\d+(\.\d+)?$/.test(normalised)) {
    return null;
  }

  const [whole, fraction = ""] = normalised.split(".");

  /* More decimal places than the token has cannot be represented, and silently
     dropping them would move money the player did not agree to. */
  if (fraction.length > decimals) {
    return null;
  }

  return BigInt(whole + fraction.padEnd(decimals, "0"));
};

/* Payout ratio for the big-win cards. Kept in BigInt to a fixed precision
   before the single conversion at the end. */
export const multiplier = (debit: string, credit: string) => {
  if (!/^\d+$/.test(debit) || !/^\d+$/.test(credit)) {
    return 0;
  }

  const staked = BigInt(debit);

  if (staked === 0n) {
    return 0;
  }

  return Number((BigInt(credit) * 100n) / staked) / 100;
};

export const formatMultiplier = (debit: string, credit: string) =>
  `${multiplier(debit, credit).toFixed(2)}x`;

/* `0xf2980245E83b8D4a483448086650E85054bB84E1` → `0xf298…84E1` */
export const shortenAddress = (address: string, lead = 6, tail = 4) => {
  if (address.length <= lead + tail + 1) {
    return address;
  }

  return `${address.slice(0, lead)}…${address.slice(-tail)}`;
};

/* How a figure and its token read together.

   A dollar-pegged stablecoin is dollars to a player: "$5.00" says what
   "5.00 USDSM" makes them work out. Anything else keeps its ticker after
   the figure, since "5.00 ETH" and "5.00 SOL" are not the same money. The
   set is by symbol, not by asking the platform, because the API has no
   "pegged to" field and these are the tokens the sites actually run on. */
const DOLLAR_PEGGED = new Set(["USDSM", "USDC", "USDT", "USD", "DAI", "USDS"]);

export const withCurrency = (amount: string, symbol: string) => {
  const ticker = symbol.trim().toUpperCase();
  if (DOLLAR_PEGGED.has(ticker)) {
    return amount.startsWith("-") ? `-$${amount.slice(1)}` : `$${amount}`;
  }
  return `${amount} ${symbol}`;
};

export const isDollarPegged = (symbol: string) => DOLLAR_PEGGED.has(symbol.trim().toUpperCase());

/* What to call the money when the figure already carries its sign: "USD"
   for a dollar stablecoin, the ticker for anything else. */
export const currencyLabel = (symbol: string) => (isDollarPegged(symbol) ? "USD" : symbol);

/* A figure the API states as a decimal string — race amounts ("1089.39"),
   prize pools, volumes — rather than in base units. Grouped and shown to the
   given decimals, truncated like formatAmount so it never claims more than
   is there. Anything that is not a plain number is returned untouched. */
export const formatDecimal = (value: string | number, significantDecimals = 2) => {
  const text = String(value).trim();
  const negative = text.startsWith("-");
  const raw = negative ? text.slice(1) : text;

  if (!/^\d+(\.\d+)?$/.test(raw)) {
    return text;
  }

  const [whole, fraction = ""] = raw.split(".");
  const grouped = groupThousands(whole);
  const sign = negative ? "-" : "";

  if (significantDecimals <= 0) {
    return `${sign}${grouped}`;
  }

  return `${sign}${grouped}.${fraction.padEnd(significantDecimals, "0").slice(0, significantDecimals)}`;
};
